/**
 * Vocabulário visual do protótipo.
 *
 * Desde a avaliação com o time (story 0.4, item 1) a interface usa a Kumo UI, e as
 * cores saem dos tokens semânticos dela — `text-kumo-success`, `bg-kumo-warning-tint` e
 * afins —, que acompanham sozinhos o modo claro e o escuro. Por isso aqui não há hexa de
 * interface: só o mapeamento de cada significado do domínio para o token certo.
 *
 * ⚠️ Toda classe fica escrita por inteiro. O Tailwind v4 descobre classes lendo o
 * código-fonte; classe montada por concatenação (`"text-kumo-" + tom`) não é encontrada
 * e sai sem estilo, sem erro nenhum.
 */

import type { Verdict } from "./fixtures/index.js";

/**
 * Rótulo de cada desfecho.
 *
 * A cor sozinha não basta: quem não distingue verde de vermelho precisa ler o desfecho,
 * e o texto é o que garante isso.
 */
export const VERDICT_LABEL: Readonly<Record<Verdict, string>> = {
  meets: "ATENDE",
  acceptable: "ACEITÁVEL",
  fails: "REPROVA",
};

/** Símbolo que acompanha o rótulo, reforçando a distinção sem depender de cor. */
export const VERDICT_SYMBOL: Readonly<Record<Verdict, string>> = {
  meets: "●",
  acceptable: "▲",
  fails: "✕",
};

/** Explicação de cada faixa, exibida ao pousar o ponteiro. */
export const VERDICT_HINT: Readonly<Record<Verdict, string>> = {
  meets: "Percentil 95 ≥ 100 tok/s e tempo até o primeiro token < 1 s",
  acceptable: "Percentil 95 entre 60 e 100 tok/s — cerca de 3× a leitura humana",
  fails: "Percentil 95 < 60 tok/s, ou tempo até o primeiro token acima de 2 s",
};

/** Variante do selo pequeno da Kumo para cada desfecho. */
export const VERDICT_BADGE_VARIANT: Readonly<Record<Verdict, "success" | "warning" | "error">> = {
  meets: "success",
  acceptable: "warning",
  fails: "error",
};

/** Classes do selo grande de cada desfecho: borda, fundo tingido e texto no mesmo tom. */
export const VERDICT_TONE_CLASS: Readonly<Record<Verdict, string>> = {
  meets: "border-kumo-success bg-kumo-success-tint text-kumo-success",
  acceptable: "border-kumo-warning bg-kumo-warning-tint text-kumo-warning",
  fails: "border-kumo-danger bg-kumo-danger-tint text-kumo-danger",
};

/** Tom de um número de destaque: neutro, ou o significado frente ao alvo. */
export type Tone = "default" | "success" | "warning" | "danger";

/** Classe de texto de cada tom. */
export const TONE_TEXT_CLASS: Readonly<Record<Tone, string>> = {
  default: "text-kumo-strong",
  success: "text-kumo-success",
  warning: "text-kumo-warning",
  danger: "text-kumo-danger",
};

/** Tom que corresponde a cada desfecho, para números que herdam o semáforo. */
export const VERDICT_TONE: Readonly<Record<Verdict, Tone>> = {
  meets: "success",
  acceptable: "warning",
  fails: "danger",
};
